import { EventEmitter, Injectable, isDevMode, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { CardAction, CardDefinition, CardModel } from '@barthauer/abc/cards-view';
import JSZip from 'jszip';

import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import {
  ExecuteQueryDocumentDef,
  QueryHelper,
  QueryObjectsModel,
  ReadDocumentModel,
  SaveDocumentModel,
} from '@barthauer/abc/core/data';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
  ProtocolViewerService,
} from '@barthauer/abc/messages';
import { AbcQueriesEditorService } from '@barthauer/abc/queries/opath/queries-editor';
import { ParameterValue } from '@barthauer/abc/queries/parameters/core';
import { AbcContainerService, AbcWindowComponent, IWindow, WindowStyle } from '@barthauer/abc/ui-components';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { BceComponent } from '../bce.component';
import { BCEDocumentBase, BCEDocumentType, BCEElement, BCEGroupPacket, BCEOpenMode } from '../models/bce.model';
import { DocumentCardBase, GroupCard } from '../viewModels/bce.viewModel';
import { DataService } from './data.service';
import { ResourceSet } from '@barthauer/abc/core/resources';
import { ParameterService } from '@barthauer/abc/queries/parameters/values-editor';
import { saveAs } from 'file-saver';
import { DataService as CoreDataService } from '@barthauer/abc/core/data';

@Injectable({
  providedIn: 'root',
})
export class BceService {
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  private windowInstance: AbcWindowComponent;
  private groupPacketParameter: BCEGroupPacket[];
  private res: ResourceSet;

  public cardModel: CardModel;
  public cardModelData: Subject<CardModel> = new Subject<CardModel>();
  @Output() selectDocumentCancelled = new EventEmitter();

  constructor(
    private abcContainer: AbcContainerService,
    private dataService: DataService,
    private abcCore: AbcCore,
    private coreDataService: CoreDataService,
    private queriesEditorService: AbcQueriesEditorService,
    private messageBoxService: MessageBoxService,
    private protocolViewerService: ProtocolViewerService,
    private parameterService: ParameterService,
    private messageBox: MessageBoxService,
    sanitizer: DomSanitizer,
    iconRegistry: MatIconRegistry,
  ) {
    iconRegistry.addSvgIcon('Opathquery', sanitizer.bypassSecurityTrustResourceUrl('assets/iconApplication.svg'));
  }

  public async bceOpen(groupPacket?: BCEGroupPacket | BCEGroupPacket[], isMobile?: boolean): Promise<void> {
    if (!isMobile) {
      await this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    this.dataService.getGroupsCardData(this.groupPacketParameter).subscribe(async (groups: GroupCard[]) => {
      this.cardModel = new CardModel(groups);
      if (this.windowInstance) {
        this.windowInstance.showLoader = false;
      }
      this.res = await this.dataService.getResourceSet();

      const primaryDocumentCardAction: CardAction = new CardAction(
        this.res.get('Edit'),
        async (cardDefinition) => {
          await this.editDocument(<BCEDocumentBase>cardDefinition.DataObject);
        },
        'Edit',
      );

      this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);
      const secondaryDocumentCardAction: CardAction = new CardAction(
        this.res.get('Execute'),
        async (cardDefinition) => {
          await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
        },
        'Execute',
      );

      this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);

      this.attachExportImportActions(this.cardModel.CardDefinitions, this.res);
      this.cardModelData.next(this.cardModel);
    });
  }
  public selectDocument(
    groupPacket?: BCEGroupPacket | BCEGroupPacket[],
    isMobile?: boolean,
    xPathFilter?: string,
    folderId?: string,
    allowTemplates?: boolean,
  ): Observable<BCEDocumentBase> {
    if (!isMobile) {
      void this.openAbcWindow();
    }
    const convertOArray = (v) => [].concat(v);
    this.groupPacketParameter = typeof groupPacket === 'string' ? convertOArray(groupPacket) : groupPacket;
    const observable$ = new Observable<BCEDocumentBase>((observer) => {
      this.dataService
        .getGroupsCardData(this.groupPacketParameter, folderId, xPathFilter, allowTemplates)
        .subscribe(async (groups: GroupCard[]) => {
          this.cardModel = new CardModel(groups);
          if (this.windowInstance) {
            this.windowInstance.showLoader = false;
          }
          this.res = await this.dataService.getResourceSet();

          const primaryDocumentCardAction: CardAction = new CardAction(
            this.res.get('Select'),
            (cardDefinition) => {
              observer.next(<BCEDocumentBase>cardDefinition.DataObject);
              if (this.windowInstance) {
                this.windowInstance.closeDialog();
              }
            },
            'Select',
          );
          this.setPrimaryActionOnDocuments(this.cardModel.CardDefinitions, primaryDocumentCardAction);

          const secondaryDocumentCardAction: CardAction = new CardAction(
            this.res.get('Execute'),
            async (cardDefinition) => {
              await this.executeDocument(<BCEDocumentBase>cardDefinition.DataObject);
            },
            'Execute',
          );
          this.setSecondaryActionOnDocuments(this.cardModel.CardDefinitions, secondaryDocumentCardAction);

          this.attachExportImportActions(this.cardModel.CardDefinitions, this.res);
          this.cardModelData.next(this.cardModel);
        });
    });
    return observable$;
  }

  private setPrimaryActionOnDocuments(viewModel: CardDefinition[], primaryDocumentCardAction: CardAction): void {
    const mode = primaryDocumentCardAction.DisplayText;
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        element.PrimaryAction = primaryDocumentCardAction;
      } else {
        if (element.CardDefinitions) {
          this.setPrimaryActionOnDocuments(element.CardDefinitions, primaryDocumentCardAction);
        }
      }
    });
  }

  private setSecondaryActionOnDocuments(viewModel: CardDefinition[], secondaryDocumentCardAction: CardAction): void {
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        //SecondaryAction is currently not needed for normal users
        if (isDevMode()) {
          element.SecondaryAction = secondaryDocumentCardAction;
        }
      } else {
        if (element.CardDefinitions) {
          this.setSecondaryActionOnDocuments(element.CardDefinitions, secondaryDocumentCardAction);
        }
      }
    });
  }

  private setExportActionOnDocuments(viewModel: CardDefinition[], exportDocumentCardAction: CardAction): void {
    this.applyDocumentAction(viewModel, (doc) => (doc.ThirdAction = exportDocumentCardAction));
  }

  private setImportActionOnDocuments(viewModel: CardDefinition[], importDocumentCardAction: CardAction): void {
    this.applyDocumentAction(viewModel, (doc) => {
      const dataObject = doc.DataObject as BCEDocumentBase | undefined;
      const isBceDoc = ConvertHelper.toBoolean((dataObject as { IsUserDocument?: boolean })?.IsUserDocument);
      doc.FourthAction = isBceDoc ? importDocumentCardAction : undefined;
    });
  }

  private applyDocumentAction<T extends BCEElement>(
    viewModel: CardDefinition[],
    assign: (doc: DocumentCardBase<T>) => void,
  ): void {
    viewModel.forEach((element) => {
      if (element instanceof DocumentCardBase) {
        assign(element);
      } else if (element.CardDefinitions) {
        this.applyDocumentAction(element.CardDefinitions, assign);
      }
    });
  }

  private attachExportImportActions(viewModel: CardDefinition[], res: ResourceSet): void {
    const exportAction = new CardAction(
      res.get('Export'),
      async (cardDefinition) => {
        await this.exportDocument(<BCEDocumentBase>cardDefinition.DataObject);
      },
      'Export',
    );
    const importAction = new CardAction(
      res.get('Import'),
      async (cardDefinition) => {
        await this.importDocument(<BCEDocumentBase>cardDefinition.DataObject);
      },
      'Import',
    );
    this.setExportActionOnDocuments(viewModel, exportAction);
    this.setImportActionOnDocuments(viewModel, importAction);
  }

  public callCloseDialog(): void {
    if (this.windowInstance) this.windowInstance.closeDialog();
  }

  private async openAbcWindow(): Promise<void> {
    const style: WindowStyle = {
      height: '80%',
      minWidth: '400px',
      width: '50%',
      resizable: true,
      shadingColor: '#ffffff80',
      shading: true,
    };
    const res = await this.getRes();
    const WindowAttr: IWindow = {
      id: 'BCEwindow',
      label: res.get('WindowTitle', 'Barthauer Configuration Explorer'),
      maximizeButton: true,
      minimizeButton: true,
      showLoader: true,
      minumizeDialogOption: -64,
      component: BceComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    this.createWindowInstance(WindowAttr);
  }
  private createWindowInstance(WindowAttr: IWindow): void {
    let windowComponentInstance = null;
    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      windowComponentInstance = activeComponent;
      activeComponent.openWindow();
    });

    windowComponentInstance.onCloseClick.subscribe(() => {
      this.selectDocumentCancelled.emit();
      this.windowInstance.showLoader = false;
    });
  }

  private async getRes(): Promise<ResourceSet> {
    if (!this.res) {
      this.res = await this.dataService.getResourceSet();
    }
    return this.res;
  }

  private async editDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Edit);
  }

  public async executeDocument(doc: BCEDocumentBase): Promise<void> {
    await this.openDocumentCore(doc, BCEOpenMode.Execute);
  }

  public async importDocument(doc: BCEDocumentBase): Promise<void> {
    if (!(await this.ensureDocumentSelected(doc))) return;
    const base64 = await this.pickXmlAsBase64();
    if (!base64) return;
    const save = await this.saveDocumentBase64(doc.Id, base64);
    const res = await this.getRes();
    if (save) {
      this.showInfo(res.get('Import-Success'), res.get('Import'));
    } else {
      this.showInfo(res.get('Import-Failed'), res.get('Import'), true);
    }
  }

  public async exportDocument(doc: BCEDocumentBase): Promise<void> {
    if (!(await this.ensureDocumentSelected(doc))) return;
    const selection = await this.promptExportFormatSelection();
    if (selection === null) return;

    const baseName = this.sanitizeFileName(doc?.Designation || 'document');
    const res = await this.getRes();

    if (selection === ExportDocumentType.bce) {
      const document = await this.fetchExportBuffer(doc.Id, 'Bce');
      await this.downloadBuffer(document, `${baseName}.bce`);
    } else if (selection === ExportDocumentType.xml) {
      const document = await this.fetchExportBuffer(doc.Id, 'Xml');
      await this.downloadBuffer(document, `${baseName}.xml`);
    } else {
      this.showInfo(res.get('No-Selection'));
      return;
    }
  }

  private async fetchExportBuffer(documentId: string, exportFormat: 'Xml' | 'Bce'): Promise<Blob | ArrayBuffer> {
    try {
      const model: ReadDocumentModel = new ReadDocumentModel();
      model.Id = documentId;
      model.Indent = true;
      model.ExportFormat = exportFormat;
      return await firstValueFrom(this.coreDataService.executeReadDocumentQueryBlob(model));
    } catch {
      return undefined;
    }
  }

  private async ensureDocumentSelected(doc: BCEDocumentBase | null | undefined): Promise<boolean> {
    if (!doc) {
      const res = await this.getRes();
      this.showInfo(res.get('No-Document-Selected'));
      return false;
    }
    return true;
  }

  private showInfo(message: string, caption?: string, allowCancel?: boolean): void {
    const settings = new MessageSettings();
    settings.caption = caption || '';
    settings.mode = MessageMode.Info;
    settings.button = allowCancel ? MessageBoxButtons.OKCancel : MessageBoxButtons.OK;
    if (this.res) {
      settings.okButtonText = this.res.get('OK');
      settings.cancelButtonText = this.res.get('Cancel');
    }
    settings.message = message;
    this.messageBox.showMessageBox(settings);
  }

  private async downloadBuffer(data: Blob | ArrayBuffer | string, fileName: string): Promise<void> {
    let blob: Blob;
    if (data instanceof Blob) {
      blob = data;
    } else {
      const type = fileName.endsWith('.xml') ? 'application/xml' : 'application/octet-stream';
      blob = new Blob([data], { type });
    }
    this.saveBlobAsFile(blob, fileName);
  }

  /**
   * Builds the export format selector UI component containing radio buttons for BCE and XML formats.
   * @param xmlAvailable Whether the XML export format should be available.
   * @param bceAvailable Whether the BCE export format should be available.
   * @returns An object containing the container element for the selector.
   */
  private async buildExportFormatSelector(xmlAvailable: boolean, bceAvailable: boolean) {
    const res = await this.getRes();
    const container = this.createEl<HTMLDivElement>('div', 'bce-export-cbx-root');

    const bceRadio = this.createRadioButton(container, 'exportFormat', 'bce', 'BCE', bceAvailable);
    const xmlRadio = this.createRadioButton(container, 'exportFormat', 'xml', 'XML', xmlAvailable);

    container.append(bceRadio, xmlRadio);

    if (bceAvailable) {
      const input = bceRadio.querySelector('input') as HTMLInputElement;
      input.checked = true;
      container.dataset.selected = 'bce';
    } else if (xmlAvailable) {
      const input = xmlRadio.querySelector('input') as HTMLInputElement;
      input.checked = true;
      container.dataset.selected = 'xml';
    }

    return { container };
  }

  private createEl<T extends HTMLElement>(tag: string, cls?: string, text?: string): T {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text !== undefined) e.textContent = text;
    return e as T;
  }

  /**
   * Creates a radio button element wrapped in a div with associated label and change logic.
   * @param container The parent container element to store the selected value in dataset.
   * @param name The name attribute for the radio input group.
   * @param value The value attribute for the radio input.
   * @param text The display text for the label.
   * @param available Whether the radio button should be visible and interactive.
   * @returns The wrapper div element containing the radio input and label.
   */
  private createRadioButton(
    container: HTMLDivElement,
    name: string,
    value: string,
    text: string,
    available: boolean,
  ): HTMLDivElement {
    const wrapper = this.createEl<HTMLDivElement>('div', 'radio-wrapper');
    wrapper.style.display = available ? 'flex' : 'none';

    const input = this.createEl<HTMLInputElement>('input');
    input.type = 'radio';
    input.name = name;
    input.value = value;
    input.id = `radio-${value}`;

    const label = this.createEl<HTMLLabelElement>('label', '', text);
    label.htmlFor = input.id;

    wrapper.append(input, label);

    const select = () => {
      if (!available) return;
      input.checked = true;
      container.dataset.selected = value;
    };

    wrapper.addEventListener('click', select);
    input.addEventListener('change', select);

    return wrapper;
  }

  private async promptExportFormatSelection(): Promise<ExportDocumentType> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Question;
    settings.button = MessageBoxButtons.OKCancel;
    const res = await this.getRes();
    settings.okButtonText = res.get('OK');
    settings.cancelButtonText = res.get('Cancel');
    settings.message = res.get('Select-File-Format', 'Bitte wählen Sie das Dateiformat aus:');
    const ui = await this.buildExportFormatSelector(true, true);
    settings.htmlcode = ui.container;
    const result = await firstValueFrom(this.messageBox.showMessageBox(settings).pipe(take(1)));
    if (DialogResult.OK === result['result']) {
      const selected = ui.container?.dataset?.selected || '';
      return selected as ExportDocumentType;
    }
    return null;
  }

  private sanitizeFileName(name: string): string {
    return (name || 'document').replace(/[^a-z0-9-_]+/gi, '_');
  }

  private readBlobText(blob: Blob, fileName: string): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string) ?? '');
      reader.onerror = () => resolve('');
      reader.readAsText(blob);
    });
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    saveAs(blob, fileName);
  }

  private async pickXmlAsBase64(): Promise<string | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.bce,.xml';

      input.onchange = async () => {
        const file = input.files?.[0] ?? null;
        if (!file) {
          resolve(null);
          return;
        }

        try {
          const arrayBuffer = await file.arrayBuffer();

          const bytes = new Uint8Array(arrayBuffer);
          const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;

          if (isZip) {
            const zip = await JSZip.loadAsync(arrayBuffer);
            const xmlFile = zip.file('content.xml');

            if (!xmlFile) {
              resolve(null);
              return;
            }

            const xmlBase64 = await xmlFile.async('base64');
            resolve(xmlBase64);
          } else {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(',')[1];
              resolve(base64);
            };
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
          }
        } catch (err) {
          resolve(null);
        }
      };

      input.click();
    });
  }

  private dataUrlToBase64(dataUrl: string): string | null {
    if (typeof dataUrl === 'string') {
      const idx = dataUrl.indexOf('base64,');
      return idx >= 0 ? dataUrl.substring(idx + 7) : null;
    }
    return null;
  }

  private async saveDocumentBase64(id: string, base64: string): Promise<boolean> {
    try {
      const model: SaveDocumentModel = new SaveDocumentModel();
      model.Id = id;
      model.Base64Content = base64;
      await firstValueFrom(this.coreDataService.executeSaveDocumentQuery(model));
      return true;
    } catch {
      return false;
    }
  }

  private async openDocumentCore(doc: BCEDocumentBase, mode: BCEOpenMode): Promise<void> {
    if (doc.documentType === BCEDocumentType.OPathQueryDocument) {
      if (await this.isDmlQueryDocument(doc.Id)) {
        if (mode === BCEOpenMode.Execute) {
          await this.executeDmlQueryDocument(doc.Id, doc.Designation);
        } else {
          throw new Error('Editing DML query documents is not supported!');
        }
      } else {
        await this.openOPathQueryDocument(doc.Id, doc.Designation, mode);
      }
    } else {
      throw new Error('Invalid document type: ' + doc.documentType);
    }
  }

  private async openOPathQueryDocument(documentId: string, title: string, mode: BCEOpenMode): Promise<void> {
    try {
      await this.queriesEditorService.runOpathQueriesEditor(documentId, title, mode === BCEOpenMode.Execute);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async isDmlQueryDocument(documentId: string): Promise<boolean> {
    if (!documentId) {
      return false;
    }
    const ids = await this.dataService.queryFilterDocuments([documentId], "//ObjectQueryDocument[@dmlType!='']");
    return await ids?.includes(documentId);
  }

  public async filterDocuments(documentIds: string[], xpathFilter: string): Promise<string[]> {
    return await this.dataService.queryFilterDocuments(documentIds, xpathFilter);
  }

  public async executeOPathQuery(query: QueryObjectsModel, title: string) {
    try {
      await this.queriesEditorService.executeQuery(query, title);
    } catch (err) {
      this.abcCore.error.publish(err);
    }
  }

  public async executeDmlQueryDocument(
    documentId: string,
    title?: string,
    parameterValues?: ParameterValue[],
  ): Promise<void> {
    const executeQueryDocumentDef: ExecuteQueryDocumentDef = new ExecuteQueryDocumentDef();
    executeQueryDocumentDef.Id = documentId;
    executeQueryDocumentDef.Parameters = parameterValues;

    const results = (await this.abcCore.data.executeQueryDocument(executeQueryDocumentDef).toPromise()) as {
      [key: string]: unknown[];
    };
    await this.showDmlProtocol(results, title);
  }

  public async executeDmlQueryDocumentWithParameters(
    documentId: string,
    title?: string,
    parameterValues?: ParameterValue[],
  ): Promise<void> {
    const readDocumentQuery: ReadDocumentModel = new ReadDocumentModel();
    readDocumentQuery.Id = documentId;
    const query: QueryObjectsModel = ConvertHelper.arrayBufferToObject(
      await this.abcCore.data.executeReadQueryFromDocumentQuery(readDocumentQuery).toPromise(),
    );

    if (!!query) {
      const parameters = this.parameterService.tryFindChangeableParameters(
        query,
        parameterValues?.map((_) => _.Name),
      );
      let customParameterValues: ParameterValue[] = await this.parameterService.fillParametersIfExist(parameters);
      if (parameters?.length && !customParameterValues?.length) {
        //Cancelled
        return;
      }

      if (parameterValues?.length) {
        if (!customParameterValues) {
          customParameterValues = [];
        }
        for (const p of parameterValues) {
          customParameterValues.push(p);
        }
      }
      QueryHelper.ensureQueryParameterValues(query, customParameterValues);
      await this.executeDmlQuery(query, title);
    } else {
      const res = await this.dataService.getResourceSet();
      this.showMessageAsHtml(res.get('Query-NotAvailable', documentId), '');
    }
  }

  public async executeDmlQuery(query: QueryObjectsModel, title?: string): Promise<void> {
    const results = await this.abcCore.data.executeManipulateObjectsQuery(query).toPromise();
    await this.showDmlProtocol(results, title);
  }

  private async showDmlProtocol(results: { [key: string]: unknown[] }, title: string): Promise<void> {
    if (results) {
      await this.protocolViewerService.openProtocolViewer(results, undefined, undefined, title);
    } else {
      const res = await this.dataService.getResourceSet();
      await this.showMessageAsHtml(res.get('Execute-NoData'), title);
    }
  }

  private async showMessageAsHtml(htmlMsg: string, title: string): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Info;
    settings.icon = MessageBoxIcon.Info;
    settings.button = MessageBoxButtons.OKCancel;
    const res = await this.getRes();
    settings.okButtonText = res.get('OK');
    settings.cancelButtonText = res.get('Cancel');
    settings.caption = title;

    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;
    await this.messageBoxService.showMessageBox(settings).toPromise();
  }
}

enum ExportDocumentType {
  'bce' = 'bce',
  'xml' = 'xml',
}
